import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * themeMode:
 * - "system": sigue prefers-color-scheme
 * - "light": fuerza light
 * - "dark": fuerza dark
 */
const STORAGE_KEY = "theme-mode";

const ThemeContext = createContext(null);

const getSystemTheme = () =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";

const applyThemeToDOM = (theme) => {
  document.documentElement.setAttribute("data-theme", theme);
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "system";
  });

  const resolvedTheme = useMemo(() => {
    if (mode === "system") return getSystemTheme();
    return mode; // "light" | "dark"
  }, [mode]);

  // Aplicar al DOM + persistir
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
    applyThemeToDOM(resolvedTheme);
  }, [mode, resolvedTheme]);

  // Escuchar cambios del SO (solo si mode === system)
  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;

    const onChange = () => {
      if (mode === "system") applyThemeToDOM(getSystemTheme());
    };

    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,                 // "system" | "light" | "dark"
      theme: resolvedTheme, // "light" | "dark"
      setMode,
    }),
    [mode, resolvedTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme debe usarse dentro de <ThemeProvider />");
  return ctx;
};